<template>
  <v-card>
    <v-card-title>
      <div>
        <div class="headline">Influencers</div>
      </div>
      <v-spacer></v-spacer>

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon
            outlined
            color="primary"
            class="mr-3"
            @click="showFavoritesOnly = !showFavoritesOnly"
          >
            <v-icon small :color="showFavoritesOnly ? 'amber darken-2' : undefined">
              {{ showFavoritesOnly ? 'fas fa-star' : 'fal fa-star' }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{ showFavoritesOnly ? 'Vis alle' : 'Vis favoritter' }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon
            outlined
            color="primary"
            class="mr-3"
            :loading="exporting"
            :disabled="exporting"
            @click="exportInfluencers"
          >
            <v-icon small>fal fa-file-csv</v-icon>
          </v-btn>
        </template>
        <span>Download CSV</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon
            outlined
            color="primary"
            @click="filtersDialog = true"
          >
            <v-icon small>fas fa-filter</v-icon>
          </v-btn>
        </template>
        <span>Filtrér</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <influencer-data-table
        :items="displayedInfluencers"
        :favorite-uuids="favoriteUuids"
        @toggle-favorite="handleToggleFavorite"
        :loading="loading"
        :page="page"
        :total="total"
        @changePage="getInfluencers($event)"
      ></influencer-data-table>
      <influencer-filter-dialog
        v-model="filtersDialog"
        :filters="filters"
        @save="(filters = $event), (page = 1)"
      ></influencer-filter-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { saveAs } from 'file-saver';
import search_filters_url from "@/helpers/mixins/search_filters_url.js";
import InfluencerDataTable from "../data-tables/InfluencerDataTable";
import InfluencerFilterDialog from "../dialogs/InfluencerFilterDialog";
import { mapActions } from "vuex";

export default {
  components: { InfluencerFilterDialog, InfluencerDataTable },
  mixins: [search_filters_url],
  data: () => ({
    filtersDialog: false,
    filters: {},
    loading: true,
    exporting: false,
    showFavoritesOnly: false,
    limit: 15,
    page: 1,
    total: null,
    influencers: [],
    favoriteUuids: [],
  }),
  watch: {
    filters: {
      handler: function() {
        this.influencers = [];
        this.getInfluencers({ page: this.page, sorting: {} });
      },
      deep: true
    },
    
    showFavoritesOnly() {
      this.getInfluencers({ page: 1 });
    }
  },
  computed: {
    
    displayedInfluencers() {
      return this.influencers;
    },
    csvData() {
      let data = [];

      for (let i = 0; i < this.influencers.length; i++) {
        let influencer = this.influencers[i];
        let influencerChannel = null;

        influencer.channels.forEach(channel => {
          if (
            !influencerChannel ||
            influencerChannel.followers < channel.followers
          ) {
            influencerChannel = channel;
          }
        });

        data.push({
          email: influencer.user.email,
          first_name: influencer.user.first_name,
          last_name: influencer.user.last_name,
          instagram_name: influencerChannel ? influencerChannel.username : "",
          instagram_followers: influencerChannel
            ? influencerChannel.followers
            : ""
        });
      }

      return data;
    }
  },
  methods: {
    ...mapActions("core", ["getInfluencers"]),
    ...mapActions("moderator", ["getModeratorInfluencers", "getModeratorFavorites", "toggleModeratorFavorite"]),

    async handleToggleFavorite(uuid) {
      const index = this.favoriteUuids.indexOf(uuid);
      if (index === -1) {
        this.favoriteUuids.push(uuid);
      } else {
        this.favoriteUuids.splice(index, 1);
      }

      try {
        const result = await this.toggleModeratorFavorite({ type: 'influencer', uuid });
        this.favoriteUuids = result.influencer_uuids;
        
        if (this.showFavoritesOnly) {
          this.getInfluencers({ page: 1 });
        }
      } catch (e) {
        if (index === -1) {
          this.favoriteUuids = this.favoriteUuids.filter(u => u !== uuid);
        } else {
          this.favoriteUuids.splice(index, 0, uuid);
        }
      }
    },

    getInfluencerParams(sorting = {}) {
      let filters = this.cloneDeep(this.filters);

      const filteredKeys = ["cities", "age_genders"];

      if (filters["category_ids"]) {
        filters["category_ids"] = filters["category_ids"].map(item =>
          Number(item)
        );
      }
      filteredKeys.forEach(key => {
        if (filters[key])
          filters[key] = filters[key].map(({ followers, id }) => ({
            followers,
            id
          }));
      });

      let params = {
        ...filters,
        active_channels: true,
        orderBy: "created_at",
        order: "desc"
      };
      if (sorting.sortBy) {
        (params.orderBy = sorting.sortBy.split(".").pop()),
          (params.order = sorting.sortDesc ? "desc" : "asc");
      }

      return params;
    },

    async exportInfluencers() {
      this.exporting = true;
      const baseUrl = `${process.env.VUE_APP_API_URL}moderator/influencers/export`;

      const params = this.getInfluencerParams();

      try {
        const response = await fetch(baseUrl, {
          method: 'POST',
          headers: {
            'Authorization': "Bearer " + this.$authHelper.getAccessToken(),
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const blob = await response.blob();
        saveAs(blob, 'make_influence_influencers.csv');

      } catch (error) {
        console.error('Export failed:', error);
      } finally {
        this.exporting = false;
      }
    },

    getInfluencers({ page = 1, sorting = {} }) {
      this.loading = true;
      this.page = page;
      this.influencers = [];
      this.setUrlSearchFilters({ ...this.filters, page: this.page });

      if (this.showFavoritesOnly) {
        if (this.favoriteUuids.length === 0) { 
          this.total = 1;
          this.loading = false;
          return;
        }
        const params = this.getInfluencerParams(sorting);
        params.influencer_uuids = this.favoriteUuids; 
        params.limit = this.limit; 
        params.page = this.page; 
        this.getModeratorInfluencers(params).then(influencers => {
          this.influencers = influencers.response;
          this.total = influencers.lastPage; 
          this.loading = false;
        });
        return; 
      }

      const params = this.getInfluencerParams(sorting);
      params.page = this.page;
      params.limit = this.limit;
      params.pinned_uuids = this.favoriteUuids;

      this.getModeratorInfluencers(params).then(influencers => {
        this.influencers = influencers.response;
        this.total = influencers.lastPage;
        this.loading = false;
      });
    }
  },
  async created() {
    this.filters = this.getUrlSearchFilters();
    const restoredPage = parseInt(this.filters.page, 10);
    delete this.filters.page;
    if (Object.keys(this.filters).length === 0) {
      this.filters.approved = true;
    }
    this.page = restoredPage > 0 ? restoredPage : 1;
    const favorites = await this.getModeratorFavorites();
    this.favoriteUuids = favorites.influencer_uuids;
    this.getInfluencers({ page: this.page });
  }
};
</script>